/**
 * The rows of the planner's downloadable reports: the Work Plan's job orders
 * and the roster's people on them, for any week (past weeks included).
 */
import { differenceInMinutes, format, parseISO } from "date-fns";

import { ManufacturingOrder } from "../production-plan/utils/calculations";
import { batchOf } from "./orders";

export interface PlanReportRow extends Record<string, string | number | null> {
	date: string;
	workcenter: string;
	product: string;
	batch: string;
	type: string;
	start: string;
	end: string;
	hours: number;
	standard: string;
	jobOrder: number;
}

export interface RosterReportRow extends Record<string, string | number | null> {
	date: string;
	workcenter: string;
	product: string;
	batch: string;
	job: string;
	person: string;
	start: string;
	end: string;
	hours: number;
	placedBy: string;
	notes: string;
}

interface Block {
	workOrderId: number;
	workerName: string;
	jobRoleName: string;
	workstation?: string | null;
	startTime: string;
	endTime: string;
	isManuallyAssigned?: boolean;
	notes?: string | null;
}

const day = (iso: string) => format(parseISO(iso), "yyyy-MM-dd");
const clock = (iso: string) => format(parseISO(iso), "yyyy-MM-dd HH:mm");
const hoursBetween = (start: string, end: string | null | undefined) => (end ? Math.round((differenceInMinutes(parseISO(end), parseISO(start)) / 60) * 100) / 100 : 0);
const productOf = (order: ManufacturingOrder | undefined) => order?.productName || order?.sku || "";

/** Every job order in the plan, in time order by line. */
export function planReportRows(orders: ManufacturingOrder[]): PlanReportRow[] {
	return orders
		.filter((o) => o.workstation)
		.slice()
		.sort((a, b) => a.startTime.localeCompare(b.startTime) || (a.workstation ?? "").localeCompare(b.workstation ?? ""))
		.map((o) => ({
			date: day(o.startTime),
			workcenter: o.workstation ?? "",
			product: productOf(o),
			batch: batchOf(o) ?? "",
			type: o.orderType && o.orderType !== "production" ? o.orderType : "production",
			start: clock(o.startTime),
			end: o.endTime ? clock(o.endTime) : "",
			hours: hoursBetween(o.startTime, o.endTime),
			standard: o.laborStandardName ?? "",
			jobOrder: o.id,
		}));
}

/** Every person's block on the roster, with the job order it is on. */
export function rosterReportRows(blocks: Block[], orders: ManufacturingOrder[]): RosterReportRow[] {
	const byId = new Map(orders.map((o) => [o.id, o]));
	return blocks
		.slice()
		.sort((a, b) => a.startTime.localeCompare(b.startTime) || (a.workstation ?? "").localeCompare(b.workstation ?? "") || a.workerName.localeCompare(b.workerName))
		.map((b) => {
			const order = byId.get(b.workOrderId);
			return {
				date: day(b.startTime),
				workcenter: b.workstation || order?.workstation || "",
				product: productOf(order),
				batch: order ? batchOf(order) ?? "" : "",
				job: b.jobRoleName,
				person: b.workerName,
				start: clock(b.startTime),
				end: clock(b.endTime),
				hours: hoursBetween(b.startTime, b.endTime),
				placedBy: b.isManuallyAssigned ? "By hand" : "Fill",
				notes: b.notes ?? "",
			};
		});
}
